import React, { useEffect, useState } from 'react';
import useNodeStore from '../store/useNodeStore';
import SpaceDialog from './SpaceDialog';
import ViewToggle from './ViewToggle';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const AddIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>;
const FolderIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 00-1.883 2.542l.857 6a2.25 2.25 0 002.227 1.932H19.05a2.25 2.25 0 002.227-1.932l.857-6a2.25 2.25 0 00-1.883-2.542m-16.5 0V6A2.25 2.25 0 016 3.75h3.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 001.06.44H18A2.25 2.25 0 0120.25 9v.776" /></svg>;
const BotIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 6.75v10.5a2.25 2.25 0 002.25 2.25zm.75-12h9v9h-9v-9z" /></svg>;
const ChevronIcon = ({ className, open }) => <svg className={`${className} transform transition-transform duration-300 ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>;
const ConversationIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 8H7M17 12H7" />
    </svg>
);
const AlertCircleIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>;
const CogIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12a7.5 7.5 0 0015 0 7.5 7.5 0 00-15 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.07.383a2.25 2.25 0 004.128 1.54l.051-.103a.75.75 0 111.34.67l-.05.103a3.75 3.75 0 01-6.88-2.57l.07-.383a.75.75 0 01-.063-.852l-.04-.02z" /></svg>;

const NodeList = () => {
    const { nodes, subscribeToNodes, loading, error } = useNodeStore();
    const navigate = useNavigate();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('create');
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('spaces_view_mode') || 'grid');

    useEffect(() => {
        localStorage.setItem('spaces_view_mode', viewMode);
    }, [viewMode]);

    useEffect(() => {
        const unsubscribe = subscribeToNodes();
        return () => unsubscribe && unsubscribe();
    }, [subscribeToNodes]);

    const handleNodeClick = (node) => {
        navigate(`/space/${node.id}`);
    };

    const handleOpenSettings = (e, nodeId) => {
        e.stopPropagation();
        setSelectedNodeId(nodeId);
        setDialogMode('edit');
        setIsDialogOpen(true);
    };

    const handleCreateNew = () => {
        setSelectedNodeId(null);
        setDialogMode('create');
        setIsDialogOpen(true);
    };

    const rootNodes = nodes.filter(node => {
        if (!node.parentId) return true;
        const parentAccessible = nodes.some(n => n.id === node.parentId);
        return !parentAccessible;
    });

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.05
            }
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 20, scale: 0.98 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: { type: "spring", stiffness: 260, damping: 20 }
        }
    };

    return (
        <div className="pb-20 min-h-full bg-[var(--bg-main)] flex flex-col font-sans selection:bg-[var(--accent-primary)]/20">
            {error && (
                <div className="bg-red-500/10 border-b border-red-500/20 px-6 py-3 flex items-start gap-3 animate-in slide-in-from-top duration-300">
                    <AlertCircleIcon className="w-5 h-5 text-red-400 mt-0.5" />
                    <p className="text-sm font-medium text-red-300">Sync Error: {error}</p>
                </div>
            )}

            <header className="px-4 md:px-8 py-8 md:py-14 max-w-7xl mx-auto w-full flex flex-col md:flex-row md:justify-between md:items-end gap-6">
                <div className="space-y-1">
                    <h1 className="text-3xl md:text-4xl font-light text-[var(--text-main)] tracking-tighter transition-all">Spaces</h1>
                    <p className="text-sm text-[var(--text-muted)] font-medium">Coordinate your projects and AI agents in dedicated environments.</p>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={handleCreateNew}
                        className="group relative flex items-center gap-2 px-4 py-2 bg-[var(--text-main)] text-[var(--bg-main)] rounded-full font-semibold transition-all hover:bg-[var(--accent-hover)] active:scale-95 shadow-lg shadow-[var(--accent-primary)]/5"
                    >
                        <AddIcon className="w-3.5 h-3.5" />
                        <span className="text-[12px] tracking-tight">New Space</span>
                    </button>
                    <ViewToggle mode={viewMode} onChange={setViewMode} />
                </div>
            </header>

            <div className="px-4 md:px-8 max-w-7xl mx-auto w-full flex-1">
                <AnimatePresence mode="wait">
                    {rootNodes.length === 0 && !loading ? (
                        <motion.div
                            key="empty-state"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="flex flex-col items-center justify-center py-32 text-center"
                        >
                            <div className="w-24 h-24 bg-[var(--bg-tertiary)]/30 backdrop-blur-md rounded-3xl flex items-center justify-center mb-6 border border-[var(--border-subtle)] shadow-2xl relative group">
                                <div className="absolute inset-0 bg-gradient-to-tr from-[var(--accent-primary)]/10 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
                                <FolderIcon className="w-10 h-10 text-[var(--text-dim)]" />
                            </div>
                            <h2 className="text-xl font-semibold text-[var(--text-main)] mb-2 tracking-tight">Empty Workspace</h2>
                            <p className="text-sm text-[var(--text-muted)] max-w-xs leading-relaxed">
                                No active spaces found. Create your first dedicated workspace to begin collaborating with Omni.
                            </p>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="list-container"
                            variants={containerVariants}
                            initial={nodes.length > 0 ? false : "hidden"}
                            animate="visible"
                            className={viewMode === 'grid'
                                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20"
                                : "flex flex-col gap-3 mb-20"}
                        >
                            <AnimatePresence>
                                {rootNodes.map((node) => (
                                    <motion.div
                                        key={node.id}
                                        variants={cardVariants}
                                        layout
                                        initial="hidden"
                                        animate="visible"
                                        exit="hidden"
                                        onClick={() => handleNodeClick(node)}
                                        className="group relative cursor-pointer"
                                    >
                                        <div className="absolute -inset-px bg-gradient-to-b from-[var(--border-medium)] to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                                        {viewMode === 'grid' ? (
                                            <div className="relative h-full p-5 bg-[var(--bg-secondary)] dark:bg-[var(--bg-tertiary)]/20 backdrop-blur-sm border border-[var(--border-subtle)] rounded-xl shadow-sm group-hover:border-[var(--border-medium)] transition-all duration-300 overflow-hidden">
                                                <div className="absolute top-0 right-0 p-3 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-all duration-300">
                                                    <button
                                                        onClick={(e) => handleOpenSettings(e, node.id)}
                                                        className="p-1.5 rounded-md bg-white/5 hover:bg-white/10 text-[var(--text-muted)] hover:text-white transition-all"
                                                        title="Settings"
                                                    >
                                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                                    </button>
                                                </div>

                                                <div className="flex flex-col h-full space-y-3.5">
                                                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-transform duration-500 group-hover:scale-105 ${node.settings?.expertId ? 'bg-[var(--text-main)] text-[var(--bg-main)] shadow-sm' : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] border border-[var(--border-subtle)]'}`}>
                                                        {node.settings?.expertId ? <BotIcon className="w-5 h-5" /> : <FolderIcon className="w-4.5 h-4.5" />}
                                                    </div>

                                                    <div className="space-y-1 pr-2">
                                                        <h3 className="text-[15px] font-medium text-[var(--text-main)] tracking-tight group-hover:text-[var(--accent-primary)] transition-colors line-clamp-1">
                                                            {node.title}
                                                        </h3>
                                                        <p className="text-[12px] text-[var(--text-muted)] font-medium line-clamp-2 leading-relaxed h-9">
                                                            {node.settings?.description || (node.settings?.path ? 'Active project workspace connected to local filesystem.' : (node.settings?.expertId ? 'Specialized agent session optimized for specific tasks.' : 'A focused environment for general collaboration.'))}
                                                        </p>
                                                    </div>

                                                    <div className="mt-auto pt-3 flex items-center justify-between border-t border-[var(--border-subtle)] transition-colors">
                                                        <div className="flex items-center gap-1.5">
                                                            <div className="w-1 h-1 rounded-full bg-emerald-500/80" />
                                                            <span className="text-[9px] font-bold text-[var(--text-dim)] uppercase tracking-wider">Active</span>
                                                        </div>
                                                        <span className="text-[9px] font-bold text-[var(--text-dim)] uppercase tracking-wider bg-[var(--bg-tertiary)]/50 px-1.5 py-0.5 rounded border border-[var(--border-subtle)]">
                                                            {node.settings?.expertId ? 'Agent' : 'Space'}
                                                        </span>
                                                    </div>
                                                </div>
                                                {/* Hover Highlight Overlay */}
                                                <div className="absolute inset-0 bg-gradient-to-tr from-[var(--accent-primary)]/[0.01] to-transparent pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </div>
                                        ) : (
                                            <div className="relative flex items-center justify-between p-3.5 bg-[var(--bg-secondary)] border border-[var(--border-subtle)] rounded-xl group-hover:border-[var(--border-medium)] transition-all duration-200 shadow-sm pr-12">
                                                <div className="flex items-center gap-4 flex-1 min-w-0">
                                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${node.settings?.expertId ? 'bg-[var(--text-main)] text-[var(--bg-main)] shadow-sm' : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] border border-[var(--border-subtle)]'}`}>
                                                        {node.settings?.expertId ? <BotIcon className="w-4 h-4" /> : <FolderIcon className="w-4 h-4" />}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="text-[14px] font-medium text-[var(--text-main)] truncate leading-tight">
                                                            {node.title}
                                                        </h3>
                                                        <p className="text-[11px] text-[var(--text-dim)] truncate mt-0.5">
                                                            {node.settings?.description || "A focused environment for collaboration."}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-6 shrink-0">
                                                    <div className="flex items-center gap-1.5">
                                                        <div className="w-1 h-1 rounded-full bg-emerald-500/80" />
                                                        <span className="text-[10px] font-bold text-[var(--text-dim)] uppercase tracking-widest leading-none">Active</span>
                                                    </div>
                                                    <span className="text-[10px] font-bold text-[var(--text-dim)] uppercase tracking-widest bg-[var(--bg-tertiary)]/50 px-2 py-0.5 rounded border border-[var(--border-subtle)] leading-none text-center min-w-[50px]">
                                                        {node.settings?.expertId ? 'Agent' : 'Space'}
                                                    </span>
                                                </div>

                                                <div className="absolute top-1/2 -translate-y-1/2 right-3 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-all duration-300">
                                                    <button
                                                        onClick={(e) => handleOpenSettings(e, node.id)}
                                                        className="p-1.5 rounded-md bg-white/5 hover:bg-white/10 text-[var(--text-muted)] hover:text-white transition-all shadow-sm"
                                                        title="Settings"
                                                    >
                                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            <SpaceDialog
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                mode={dialogMode}
                nodeId={selectedNodeId}
            />
        </div>
    );
};

export default NodeList;
